import { useState, FormEvent, useMemo } from 'react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { Button, Modal } from '@components/common';
import { Input, Label, RadioText } from '@components/common/Forms';
import { useTranslation, Trans } from 'next-i18next';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { useSwrClaimWorkplace } from '@hooks/useSwrApi/endpoints/workplace';

// Interface
// ******************************************
interface ClaimWorkplaceModalProps {
  workplaceSlug: string;
  workplaceName: string;
}

/**
 * @description ClaimWorkplaceModal component.  It allows users to claim ownership of a workplace,
 * by specifying their occupation within the company.
 *
 * - **workplaceSlug** - string - The workplace slug.
 * - **workplaceName** - string - The display name of the workplace.
 */
export const ClaimWorkplaceModal = NiceModal.create((props: ClaimWorkplaceModalProps) => {
  // Destructure props
  const { workplaceSlug, workplaceName } = props;

  // Translation
  const { t } = useTranslation('common');

  // Router
  const { router, locale } = useExtendedRouter();

  // Modal instance from `NiceModal`
  const modal = useModal();

  // State
  // ******************************************
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [occupation, setOccupation] = useState('');

  // SWR Mutation
  const {
    trigger, isMutating,
  } = useSwrClaimWorkplace(workplaceSlug, locale, () => {
    void router.push(`/company/edit?workplaceSlug=${workplaceSlug}`);
  });

  // Memoized condition for disabling the confirm button
  const isDisabled = useMemo(
    () => isMutating || !(isCheckboxChecked && occupation.trim().length >= 2),
    [isMutating, isCheckboxChecked, occupation],
  );

  // Handlers
  // ******************************************
  // Close modal
  const closeModal = () => {
    if (!isMutating) {
      void modal.hide();
    }
  };

  // Submit form
  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();

    void trigger({
      experienceOccupation: occupation,
    });
  };

  // Render component
  // *****************************************************
  return (
    <Modal isOpen={modal.visible} onClose={closeModal} size="sm">
      <form onSubmit={handleSubmit}>
        <Modal.Header onClose={closeModal}>
          <Trans
            i18nKey="claim-profile.modal.title"
            values={{ company: workplaceName }}
            shouldUnescape
          />
        </Modal.Header>
        <Modal.Body>
          <p className="text-sm font-semibold text-primary">
            { t('claim-profile.modal.authorization-notice') }
          </p>

          <Label
            htmlFor="claim-checkbox"
            defaultMargin={false}
            className="mt-6 flex cursor-pointer items-center space-x-3"
          >
            <Input
              id="claim-checkbox"
              className="peer cursor-pointer text-primary transition-colors duration-200 checked:bg-primary"
              type="checkbox"
              checked={isCheckboxChecked}
              onChange={(e) => setIsCheckboxChecked(e.target.checked)}
            />
            <RadioText className="pt-px text-sm !leading-normal text-ink-medium transition-colors duration-200 peer-checked:text-ink">
              <Trans
                i18nKey="claim-profile.modal.confirmation"
                values={{ company: workplaceName }}
                shouldUnescape
              />
            </RadioText>
          </Label>

          <Label
            htmlFor="experience"
            description={t('global.label.experience_position')}
            descriptionClass="font-bold text-ink-medium"
            className="!mt-6 block"
            required
          >
            <Input
              type="text"
              required
              minLength={2}
              placeholder={t('global.placeholder.experience_position')}
              value={occupation}
              onChange={(e) => setOccupation(e.target.value)}
            />
          </Label>
        </Modal.Body>
        <Modal.Footer className="flex justify-end">
          <Button
            type="submit"
            color="secondary"
            rounding="full"
            disabled={isDisabled}
            isLoading={isMutating}
          >
            {t('global.action.claim')}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
});
